import { useState } from 'react';
import { aboutData } from '@/mocks/data';

export default function AboutPage() {
  return (
    <main className="min-h-screen pt-16">
      <BrandSection />
      <StatsSection />
      <MilestonesSection />
      <TestimonialsSection />
      <ContactSection />
    </main>
  );
}

function BrandSection() {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cherryvideo.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://cherryvideo.com/about' },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <section className="relative bg-background-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
            关于 <span className="text-primary-500">樱桃视频</span>
          </h1>
          <div className="w-20 h-1 bg-accent-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-base text-foreground-700 leading-relaxed max-w-3xl mx-auto mb-6">
            {aboutData.brandIntro}
          </p>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 max-w-2xl mx-auto">
            <p className="text-sm text-foreground-600 leading-relaxed italic">
              &ldquo;{aboutData.mission}&rdquo;
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function StatsSection() {
  return (
    <section className="py-14 md:py-16 bg-background-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {aboutData.stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MilestonesSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
          发展历程
        </h2>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
          <div className="flex flex-col gap-8">
            {aboutData.milestones.map((item, index) => (
              <div
                key={index}
                className={`relative flex items-start gap-6 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className="hidden md:block flex-1"></div>
                <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 flex-shrink-0 mt-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                </div>
                <div className="flex-1 bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <span className="text-xs font-semibold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {item.year}
                  </span>
                  <p className="text-sm text-foreground-700 mt-2 leading-relaxed">{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          用户口碑
        </h2>
        <p className="text-sm text-foreground-600 text-center mb-10">
          听听用户们怎么说
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {aboutData.testimonials.map((t, index) => (
            <div
              key={index}
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex flex-col"
            >
              <p className="text-sm text-foreground-700 leading-relaxed flex-1 mb-5 italic">
                &ldquo;{t.content}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-10 h-10 rounded-full object-cover"
                  title={t.name}
                />
                <div>
                  <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                  <div className="text-xs text-foreground-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setErrorMsg('留言内容不能超过500个字符');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const formDataObj = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9ido86c26n1c7c5psng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setErrorMsg(msg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请稍后再试');
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-14 md:py-20 bg-background-100">
      <div className="max-w-2xl mx-auto px-4 md:px-6">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          联系我们
        </h2>
        <p className="text-sm text-foreground-600 text-center mb-8">
          有任何问题或建议？欢迎与我们联系
        </p>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
          <form data-readdy-form onSubmit={handleSubmit} noValidate>
            <div className="mb-5">
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                您的姓名
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                placeholder="请输入您的姓名"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                电子邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                placeholder="请输入您的邮箱"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                留言内容
                <span className="text-foreground-400 ml-1">（最多500字）</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={5}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                placeholder="请输入您的留言或问题..."
              />
              <div className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</div>
            </div>

            <input
              name="website_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            {status === 'error' && errorMsg && (
              <div className="mb-5 p-3 bg-primary-100/70 border border-primary-200 rounded-md text-sm text-primary-700">
                {errorMsg}
              </div>
            )}

            {status === 'success' && (
              <div className="mb-5 p-3 bg-accent-100/80 border border-accent-200 rounded-md text-sm text-accent-700">
                感谢您的留言！我们会在24小时内回复您。
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-md transition-all duration-200 text-sm cursor-pointer whitespace-nowrap"
            >
              {status === 'loading' ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中...
                </span>
              ) : (
                '发送留言'
              )}
            </button>
          </form>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-mail-line text-accent-600"></i>
            </div>
            <div className="text-xs text-foreground-500 mb-1">电子邮箱</div>
            <div className="text-sm font-medium text-foreground-800">{aboutData.contact.email}</div>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-phone-line text-accent-600"></i>
            </div>
            <div className="text-xs text-foreground-500 mb-1">客服电话</div>
            <div className="text-sm font-medium text-foreground-800">{aboutData.contact.phone}</div>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-map-pin-line text-accent-600"></i>
            </div>
            <div className="text-xs text-foreground-500 mb-1">公司地址</div>
            <div className="text-sm font-medium text-foreground-800">{aboutData.contact.address}</div>
          </div>
        </div>
      </div>
    </section>
  );
}